<template>
  <div class="order-form">
    <h2 class="order-title">
      <template v-if="!currentOrder.id">
        Новый заказ
      </template>
      <template v-else>
        Заказ #{{ currentOrder.order_number }} на {{ currentOrder.amount }} {{ currentOrder.currency }}
      </template>
    </h2>
    <el-steps :active="activeStep" finish-status="success" align-center>
      <el-step title="Сумма заказа" />
      <el-step title="Данные отправителя" />
      <el-step title="Данные получателя" />
    </el-steps>

    <div class="form-container" v-loading="loading" element-loading-text="Загрузка...">
      <div v-show="activeStep === 0">
        <order-details />
      </div>

      <div v-show="activeStep === 1">
        <sender-information/>
      </div>

      <div v-show="activeStep === 2">
        <payee-information/>
      </div>

      <div class="form-actions">
        <el-button v-if="activeStep > 0" @click="prevStep">Назад</el-button>
        <el-button
          type="primary"
          v-if="activeStep < 2"
          @click="nextStep"
          :disabled="!isCurrentStepValid"
          :loading="loading"
        >
          Вперед
        </el-button>
        <el-button
          type="primary"
          v-if="activeStep === 2"
          @click="completeOrder"
          :disabled="!isCurrentStepValid"
          :loading="loading"
        >
          Оформить заказ
        </el-button>
      </div>
    </div>

    <!-- Agreement Document Dialog -->
    <el-dialog
      v-model="showAgreementDialog"
      :title="agreementDialogTitle"
      width="90%"
      class="responsive-dialog"
      center
      :close-on-click-modal="false"
      :close-on-press-escape="false"
      :show-close="false"
    >
      <div class="agreement-dialog-content">
        <p>{{ agreementDialogMessage }}</p>
      </div>
      <template #footer>
        <div class="dialog-footer">
          <el-button 
            v-if="currentOrder.id && currentOrder.sender"
            type="primary"
            tag="a"
            :href="`/api/orders/${currentOrder.id}/download-agreement`"
            target="_blank"
          >
            Скачать
          </el-button>
          <el-button @click="continueAfterAgreementDialog">Далее</el-button>
        </div>
      </template>
    </el-dialog>

    <!-- Documents Ready Dialog -->
    <el-dialog
      v-model="showDocumentsReadyDialog"
      title="Поручение и счет готовы"
      width="90%"
      class="responsive-dialog"
      center
      :close-on-click-modal="true"
      :close-on-press-escape="true"
      :show-close="true"
      @closed="handleDocumentsDialogClosed"
    >
      <div class="documents-dialog-content">
        <p>Поручение и счет для вашего заказа готовы. Вы можете скачать их, нажав на соответствующие кнопки.</p>
      </div>
      <template #footer>
        <div class="dialog-footer">
          <el-button 
            type="primary"
            tag="a"
            :href="`/api/orders/${currentOrder.id}/download-assignment`"
            target="_blank"
          >
            Скачать поручение
          </el-button>
          <el-button 
            type="primary"
            tag="a"
            :href="`/api/orders/${currentOrder.id}/download-invoice`"
            target="_blank"
          >
            Скачать счет
          </el-button>
        </div>
      </template>
    </el-dialog>

    <!-- Order Amount and Commission Dialog -->
    <el-dialog
      v-model="showOrderAmountDialog"
      title="Информация о заказе"
      width="90%"
      class="responsive-dialog"
      center
      :close-on-click-modal="true"
      :close-on-press-escape="true"
      :show-close="true"
    >
      <div class="order-amount-dialog-content">
        <h3>Детали заказа</h3>
        <div class="order-amount-details">
          <div class="detail-row">
            <span class="detail-label">Сумма заказа:</span>
            <span class="detail-value">{{ currentOrder.amount }} {{ currentOrder.currency }}</span>
          </div>
          <div class="detail-row" v-if="currentOrder.convertation_commission">
            <span class="detail-label">Комиссия агента:</span>
            <span class="detail-value">{{ currentOrder.convertation_commission * 100 }}%</span>
          </div>
          <div class="detail-row" v-if="currentOrder.swift_commission">
            <span class="detail-label">Комиссия SWIFT:</span>
            <span v-if="currentOrder.swift_commission > 1" class="detail-value">{{ currentOrder.swift_commission }} {{ currentOrder.currency }}</span>
            <span v-else class="detail-value">{{ currentOrder.swift_commission * 100 }}%</span>
          </div>
          <div class="detail-row" v-if="currentOrder.amount_to_pay">
            <span class="detail-label">К оплате рублями:</span>
            <span class="detail-value">{{ currentOrder.amount_to_pay }}</span>
          </div>
        </div>
      </div>
      <template #footer>
        <div class="dialog-footer">
          <el-button type="primary" @click="showOrderAmountDialog = false">Продолжить</el-button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, computed } from 'vue'
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'
import { ElMessage } from 'element-plus'
import { type Sender, type Payee, DocumentsStatusEnum } from '@/store/types'
import OrderDetails from '@/components/order/OrderDetails.vue'
import SenderInformation from '@/components/order/SenderInformation.vue'
import PayeeInformation from '@/components/order/PayeeInformation.vue'
import router from '@/router'

export default defineComponent({
  name: 'OrderForm',
  components: {
    OrderDetails,
    SenderInformation,
    PayeeInformation
  },
  setup() {
    const store = useStore()
    const route = useRoute()
    const loading = ref(false)
    const showAgreementDialog = ref(false)
    const showDocumentsReadyDialog = ref(false)
    const showOrderAmountDialog = ref(false)
    
    const currentOrder = computed(() => store.state.currentOrder)
    const userEmail = computed(() => store.state.auth?.user?.email || '')
    const activeStep = computed(() => store.state.activeOrderStep)

    const agreementDialogTitle = computed(() => {
      return currentOrder.value.sender?.agreement_document_url
        ? "Договор готов"
        : "Договор в подготовке"
    })

    const agreementDialogMessage = computed(() => {
      if (currentOrder.value.sender?.agreement_document_url) {
        return `Договор с ${currentOrder.value.sender?.name || ''} готов!`
      } else {
        return `Менеджер уже готовит договор с ${currentOrder.value.sender?.name || ''}. Как только договор будет готов, вы получите его на почту ${userEmail.value}.`
      }
    })

    const isCurrentStepValid = computed(() => {
      switch (activeStep.value) {
        case 0:
          return store.getters.isOrderDetailsValid
        case 1:
          return store.getters.isSenderInformationValid
        case 2:
          return store.getters.isPayeeInformationValid
        default:
          return false
      }
    })

    const fetchOrderById = async (id: string) => {
      try {
        loading.value = true
        await store.dispatch('loadCurrentOrderByExistingId', id)
      } catch (error) {
        ElMessage.error('Failed to load order')
      } finally {
        loading.value = false
      }
    }

    onMounted(async () => {
      try {
        // Reset active step when mounting
        store.commit('SET_ACTIVE_ORDER_STEP', 0)
        
        // Load initial data
        await store.dispatch('fetchSenders')
        await store.dispatch('fetchPayees')
        
        // Check if we have an order ID in the route
        const orderId = route.params.id
        if (orderId && orderId !== 'new') {
          await fetchOrderById(orderId as string)
        }
      } catch (error) {
        ElMessage.error('Failed to load data')
        console.error('Error loading data:', error)
      }
    })

    const continueAfterAgreementDialog = () => {
      showAgreementDialog.value = false
      store.commit('SET_ACTIVE_ORDER_STEP', activeStep.value + 1)
    }

    const completeOrder = async () => {
      if (activeStep.value === 2 && isCurrentStepValid.value) {
        try {
          loading.value = true
          
          // Save the payee information
          const orderData = store.state.currentOrder
          
          if (orderData.payee_id) {
            await store.dispatch('updatePayee', orderData.payee)
          } else {
            await store.dispatch('createPayee', orderData.payee)
          }

          await store.dispatch('setPayeeForCurrentOrder', orderData.payee)
          
          // Show documents ready dialog
          showDocumentsReadyDialog.value = true
          
        } catch (error) {
          ElMessage.error('Ошибка при сохранении заказа')
        } finally {
          loading.value = false
          window.scrollTo({ top: 0, behavior: 'smooth' })
        }
      }
    }

    const nextStep = async () => {
      if (activeStep.value < 2 && isCurrentStepValid.value) {
        try {
          loading.value = true
          
          // Save the current order state to the backend
          const orderData = store.state.currentOrder
          
          if (activeStep.value === 0) {
            if (orderData.id) {
              // Update existing order
              await store.dispatch('updateOrder', orderData)
            } else {
              console.log('createOrder', orderData)
              await store.dispatch('createOrder', orderData)
              // Update URL after order creation
              router.push(`/order/${store.state.currentOrder.id}`)
            }

            showOrderAmountDialog.value = true
          }
          if (activeStep.value === 1) {
            if (orderData.sender_id) {
              await store.dispatch('updateSender', orderData.sender)
            } else {
              await store.dispatch('createSender', orderData.sender)
            }
            await store.dispatch('setSenderForCurrentOrder', orderData.sender)

            if (store.state.currentOrder.sender.signed_agreement_status !== DocumentsStatusEnum.OK) {
              showAgreementDialog.value = true
              return
            }
          }
          
          store.commit('SET_ACTIVE_ORDER_STEP', activeStep.value + 1)
          window.scrollTo({ top: 0, behavior: 'smooth' })
        } catch (error) {
          ElMessage.error('Ошибка при сохранении заказа')
        } finally {
          loading.value = false
          window.scrollTo({ top: 0, behavior: 'smooth' })
        }
      }
    }

    const prevStep = () => {
      if (activeStep.value > 0) {
        store.commit('SET_ACTIVE_ORDER_STEP', activeStep.value - 1)
        window.scrollTo({ top: 0, behavior: 'smooth' })
      }
    }

    const handleDocumentsDialogClosed = () => {
      router.push('/orders')
    }

    return {
      activeStep,
      loading,
      isCurrentStepValid,
      nextStep,
      prevStep,
      currentOrder,
      showAgreementDialog,
      showDocumentsReadyDialog,
      showOrderAmountDialog,
      agreementDialogTitle,
      agreementDialogMessage,
      continueAfterAgreementDialog,
      completeOrder,
      handleDocumentsDialogClosed
    }
  }
})
</script>

<style lang="scss" scoped>
.order-form {
  max-width: 70%;
  margin: 0 auto;
  box-sizing: border-box;
}

.order-title {
  text-align: center;
  margin-bottom: 20px;
  font-size: 1.5rem;
  color: #303133;
}

.form-container {
  margin-top: 30px;
  margin-bottom: 50px;
  position: relative;
  min-height: 200px;
}

.form-actions {
  margin-top: 30px;
  display: flex;
  justify-content: space-between;
}

.agreement-dialog-content,
.documents-dialog-content,
.order-amount-dialog-content {
  text-align: center;
  margin: 20px 0;
  font-size: 16px;
}

.order-amount-details {
  max-width: 500px;
  margin: 0 auto;
  text-align: left;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
  padding: 5px 0;
  border-bottom: 1px solid #eee;
}

.detail-label {
  font-weight: 600;
}

.detail-value {
  font-weight: 500;
}

.dialog-footer {
  display: flex;
  justify-content: center;
  gap: 20px;
}

:deep(.el-step__title) {
  white-space: nowrap;
  font-size: 14px;
}

@media (max-width: 768px) {
  .order-form {
    max-width: 85%;
  }
  
  :deep(.el-step__title) {
    font-size: 12px;
  }
  
  :deep(.el-step__icon) {
    margin-right: 4px;
  }
  
  :deep(.el-step) {
    padding-right: 10px;
  }
  
  .form-actions {
    flex-wrap: wrap;
    gap: 10px;
  }
  
  .form-actions .el-button {
    flex: 1;
    min-width: 100px;
  }
  
  .dialog-footer {
    flex-direction: column;
    width: 100%;
    gap: 10px;
  }
  
  .dialog-footer .el-button {
    margin: 0;
    width: 100%;
  }
  
  :deep(.responsive-dialog) {
    .el-dialog__header {
      padding: 15px;
    }
    
    .el-dialog__body {
      padding: 15px;
      font-size: 14px;
    }
    
    .el-dialog__title {
      font-size: 16px;
    }
    
    .el-dialog__footer {
      padding: 10px 15px 20px;
    }
  }
}
</style> 